import React, { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { FULFILLED } from "constants/index"
import { searchUser } from "utils/componentsUtils"
import { useAppSelector } from "appRedux/hooks"
import { updateGlobalToastMessage } from "appRedux/slices/globalSlice"
import { useLazyGetFollowersQuery, useUpdateCloseFriendsMutation } from "appRedux/slices/userApiSlice"
import useCloseFriends from "hooks/useCloseFriends"
import SearchInput from "components/atoms/SearchInput"
import CTA from "components/atoms/CTA"
import CloseFriendsForm from "components/organisms/CloseFriendsForm"

export default function UpdateCloseFriendsTemplate() {
  const [searchedValue, setSearchedValue] = useState<string>("")
  const [initialCloseFriends, setInitialCloseFriends] = useState<UserDto[] | null>(null)
  const [localCloseFriends, setLocalCloseFriends] = useState<UserDto[]>([])
  const [localNonCloseFriends, setLocalNonCloseFriends] = useState<UserFollowDto[]>([])
  const [closeFriendIds, setCloseFriendIds] = useState<number[]>([])

  const userProfile = useAppSelector((state) => state.user.profile)
  const followers = useAppSelector((state) => state.profileTabs.followers)

  const [triggerUpdateCloseFriends, updateCloseFriendsResult] = useUpdateCloseFriendsMutation()
  const [triggerGetFollowers] = useLazyGetFollowersQuery()

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { isFetching, closeFriends } = useCloseFriends()

  const categorizeFollowersBasedOnCloseFriendStatus = useCallback((users: UserFollowDto[]) => {
    if (isFetching) return
    if (closeFriends === null) return
    const closeFriendIds = new Set((closeFriends).map(user => user.id))
    const nonCloseFollowers = users.filter(user => !closeFriendIds.has(user.id))

    setLocalCloseFriends(closeFriends)
    setLocalNonCloseFriends(nonCloseFollowers)

    if (initialCloseFriends === null) {
      setInitialCloseFriends(closeFriends)
    }
  }, [closeFriends, isFetching])

  useEffect(() => {
    if (localCloseFriends.length && !followers.length) return
    categorizeFollowersBasedOnCloseFriendStatus(followers)
  }, [followers, closeFriends, isFetching])

  useEffect(() => {
    if (updateCloseFriendsResult.status === FULFILLED) {
      handleCloseFriendsUpdatedSuccessfully()
    }
  }, [updateCloseFriendsResult])

  function handleCloseFriendsUpdatedSuccessfully() {
    navigate(-1)

    dispatch(updateGlobalToastMessage({
      severity: "success",
      key: "closeFriendsUpdatedSuccessfully",
      show: true,
      message: "successMessage.savedSuccessfully"
    }))

    if (!userProfile?.id) return
    triggerGetFollowers(userProfile?.id)
  }

  function handleSearchUser(event: React.ChangeEvent<HTMLInputElement>) {
    const searchedValue = event.target.value
    setSearchedValue(searchedValue)

    const filteredUsers = searchUser(followers, searchedValue)
    categorizeFollowersBasedOnCloseFriendStatus(filteredUsers)
  }

  function handleChangeChosenCloseFriends(ids: number[]) {
    setCloseFriendIds(ids)
  }

  function isSubmitButtonDisabled() {
    if (initialCloseFriends === null) return true
    if (closeFriendIds.length !== initialCloseFriends.length) return false

    const initialCloseFriendIds = initialCloseFriends.map(friend => friend.id)

    const currentCloseFriendIdsSet = new Set(closeFriendIds)
    const initialCloseFriendIdsSet = new Set(initialCloseFriendIds)

    for (const id of currentCloseFriendIdsSet) {
      if (!initialCloseFriendIdsSet.has(id)) return false
    }

    return true
  }

  function handleSubmit() {
    triggerUpdateCloseFriends({ userIds: closeFriendIds })
  }

  return (
    <Container>
      <FixedSearchInputContainer>
        <SearchInput
          id={"search-list-input"}
          placeholder={t("general.searchUser")}
          defaultValue={searchedValue}
          onChange={handleSearchUser}
        />
      </FixedSearchInputContainer>

      <CloseFriendsForm
        closeFriendUsers={localCloseFriends}
        nonCloseFriendUsers={localNonCloseFriends}
        onChange={handleChangeChosenCloseFriends}
      />

      <FixedCTAContainer>
        <CTA
          variant="contained"
          disabled={isSubmitButtonDisabled()}
          onClick={handleSubmit}
        >
          {t("general.save")}
        </CTA>
      </FixedCTAContainer>
    </Container>
  )
}

const Container = styled(Stack)(({ theme }) => ({
  height: "calc(100% - 120px)",
  marginBottom: "8px",
  gap: "8px",
  overflow: "hidden",

  "& .MuiTextField-root": {
    marginBottom: "0 !important"
  }
}))

const FixedSearchInputContainer = styled(Stack)(({ theme }) => ({
  width: "calc(100vw - 32px)",
  maxWidth: "calc((var(--max-container-width) - 32px))",
  position: "fixed",
  top: 48,
  padding: "8px 0",
  zIndex: 1000,
  backgroundColor: theme.palette.gray["2"],
  borderBottom: `1px solid ${theme.palette.gray["3"]}`
}))

const FixedCTAContainer = styled(Stack)(({ theme }) => ({
  width: "calc(100vw - 32px)",
  maxWidth: "calc((var(--max-container-width) - 32px))",
  position: "fixed",
  bottom: 30,
  padding: "8px 0 0 0",
  zIndex: 1000,
  backgroundColor: theme.palette.gray["2"],
  borderTop: `1px solid ${theme.palette.gray["3"]}`
}))
